import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LoadingOutlined, UserOutlined } from '@ant-design/icons'
import { AutoComplete, Button, Space, Spin } from 'antd'
import { debounce } from '../../helpers/helpers'
import api from '../../axios'
import { isJsonString } from '../../helpers/helpers'
import '../Hands.module.scss'
import { useComponentLocale } from '../../hooks/useComponentLocale'

export default function Complete({ filter, isHero, modifyPlayerFilter, savedNames, isAgainstVillain }) {
	const [value, setValue] = useState('')
	const [names, setNames] = useState([])
	const [aliases, setAliases] = useState([])
	const [isLoading, setLoading] = useState(false)
	const [isAliasesFetched, setAliasesFetched] = useState(false)

	const firstUpdate = useRef(true)
	useLayoutEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false

			return
		}
	})

	const getLocale = useComponentLocale()
	const noItemFoundLocale = getLocale({ component: 'hands', section: 'noItemFound' })
	const playersLocale = getLocale({ component: 'hands', section: 'players' })
	const playerName = getLocale({ component: 'hands', section: 'playerName' })
	const aliasesLocale = getLocale({ component: 'navigation', section: 'aliases' })
	const againstVillain = getLocale({ component: 'hands', section: 'against villain' })

	const antIcon = <LoadingOutlined style={{ fontSize: 14 }} spin />

	const makeQuery = useMemo(
		() => async (data) => {
			setLoading(true)
			const response = await api.post('get_player_names', {
				substring: data,
				isHero: isAgainstVillain ? false : isHero,
				offset: 0,
				limit: 100,
			})
			const { names, aliases } = response.data
			setNames(names)
			setAliases(aliases)
			setLoading(false)

			setAliasesFetched(false)
		},
		[isHero, filter.is_hero, filter.hero]
	)

	/**
	 * we should use useMemo hook to prevent redeclaration of debounced function
	 * This caused by each time executing same block of component function on each setState avocation
	 * */
	const fetchData = useMemo(
		() =>
			debounce((searchText) => {
				makeQuery(searchText)
				setAliasesFetched(false)
			}, 1300),
		[isHero]
	)

	const renderOptions = useMemo(() => {
		if (names.length <= 0 && aliases.length <= 0) {
			return []
		}

		return [
			{
				label: <span>{aliasesLocale}</span>,
				options: [
					...aliases.map((item) => ({
						value: JSON.stringify(item),
						label: (
							<Space style={{ display: 'flex', alignItems: 'center' }}>
								<div
									style={{
										borderRadius: '50%',
										width: 10,
										height: 10,
										backgroundColor: item.color,
									}}
								/>
								<span>{item.aliasName}</span>
							</Space>
						),
					})),
				],
			},
			{
				label: <span>{playersLocale}</span>,
				options: [
					...names.map((item) => ({
						value: item,
						label: (
							<Space>
								<UserOutlined fill='#333' />
								<span>{item}</span>
							</Space>
						),
					})),
				],
			},
		]
	}, [names, aliases, isHero])

	const onSelect = (data) => {
		if (isJsonString(data)) {
			if (isHero === null) {
				;['hero', 'villain'].forEach((player) => {
					modifyPlayerFilter(JSON.parse(data), 'add', player, true)
				})
			} else {
				modifyPlayerFilter(JSON.parse(data), 'add', isHero ? 'hero' : 'villain', true)
			}
		} else {
			if (isHero === null) {
				;['hero', 'villain'].forEach((player) => {
					modifyPlayerFilter(data, 'add', player, false)
				})
			} else {
				modifyPlayerFilter(data, 'add', isHero ? 'hero' : 'villain', false)
			}
		}

		setValue('')
		setNames([])
		setAliases([])
	}

	const onChange = (data) => {
		// limit for number of input characters
		if (data.length <= 19) {
			setValue(data)
		}
	}

	const addPressed = (value, mode, typeOfPlayer) => {
		modifyPlayerFilter(value, mode, typeOfPlayer, false)

		setValue(null)
		setAliases([])
		setNames([])
	}

	useEffect(() => {
		if (savedNames) {
			savedNames.forEach((name) => {
				makeQuery(name)
			})
		}
	}, [])

	useEffect(() => {
		if (aliases.length > 0 && savedNames) {
			if (savedNames.includes(aliases[0].aliasName)) {
				onSelect(JSON.stringify(aliases[0]))
			}
		}
	}, [aliases])

	useEffect(() => {
		if (value) {
			makeQuery(value)
		}
		setAliasesFetched(false)
	}, [isHero])

	return (
		<div style={{ width: '100%', display: 'flex' }}>
			<AutoComplete
				style={{ flexGrow: 1 }}
				options={renderOptions}
				onSelect={onSelect}
				dropdownClassName='long-dropdown-modifier'
				dropdownMatchSelectWidth={false} /* todo: remove this if width of dropdown is not okay*/
				onClick={() => {
					if (value.length === 0 && !isAliasesFetched) {
						makeQuery('')
						setAliasesFetched(true)
					}
				}}
				onChange={(data) => {
					onChange(data)
					fetchData(data)
					// fetch data only if substring is longer than one string
					/* if (data.length >= 1)
						fetchData(data);*/
				}}
				size='small'
				value={value}
				placeholder={isAgainstVillain ? againstVillain : playerName}
				notFoundContent={isLoading ? '' : noItemFoundLocale}
			/>
			<Button
				disabled={isLoading || !value || isHero === null}
				size='small'
				style={{ marginLeft: 3, width: 30 }}
				onClick={() => {
					addPressed(value, 'add', isHero ? 'hero' : 'villain')
				}}
			>
				{isLoading ? <Spin indicator={antIcon} /> : '+'}
			</Button>
		</div>
	)
}
